import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';

/**
 * Full-screen video modal.
 * Props:
 *   isOpen    – boolean
 *   onClose   – () => void
 *   videoUrl  – string (mp4/webm)
 *   title     – string (optional)
 */
export default function VideoModal({ isOpen, onClose, videoUrl, title }) {
  const videoRef = useRef(null);

  // Close on ESC key
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  // Pause & reset when modal closes
  useEffect(() => {
    if (!isOpen && videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      data-testid="video-modal"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/90 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="relative z-10 w-full max-w-6xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-3 px-1">
          {title && (
            <span className="text-white font-semibold text-sm truncate max-w-[80%]">{title}</span>
          )}
          <button
            onClick={onClose}
            className="ml-auto w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-all"
            aria-label="Close video"
            data-testid="video-modal-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Video */}
        <div className="rounded-2xl overflow-hidden shadow-2xl shadow-black/60 border border-white/10 bg-black aspect-video">
          <video
            ref={videoRef}
            src={videoUrl}
            controls
            autoPlay
            className="w-full h-full object-contain"
            data-testid="video-modal-player"
          />
        </div>
      </div>
    </div>
  );
}
